import React, { Key, useState } from "react";
import * as S from "./index.styles";
import Avatar from "@mui/material/Avatar";
import PersonIcon from "@mui/icons-material/Person";
import DoubleChevronIcon from "components/icons/DoubleChevronIcon";
import DownloadCSVButton from "components/common/DownloadCSVButton";


export interface TopDonorsListDonor {
  id: string | number;
  avatar?: string;
  name: string;
  date: string;
  amount: number;
  isAnonymous?: boolean;
  displayAmount?: boolean;
  totalDonors?: number;
  showTopDonorAvatar?: boolean;
  showTopDonorName?: boolean;
  showDownloadButton?: boolean;
}

interface Props {
  title: string;
  donors: TopDonorsListDonor[];
  donorsToShowInitially?: number;
  showTotalDonors?: boolean;
  showTopDonorAvatar?: boolean;
  showTopDonorName?: boolean;
  showDownloadButton?: boolean;
}

const TopDonorsList: React.FC<Props> = ({
  title,
  donors = [],
  donorsToShowInitially = 5,
  showTotalDonors,
  showTopDonorAvatar,
  showTopDonorName,
  showDownloadButton,
}) => {
  const [showAllDonors, setShowAllDonors] = useState(false);

  const displayedDonors = showAllDonors
    ? donors
    : donors.slice(0, donorsToShowInitially);

  return (
    <div style={{width: '100%'}}>
      <S.HeaderContainer>
        <S.TitleContainer>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <S.SectionCaption>{title}</S.SectionCaption>
            {showDownloadButton && (
                <DownloadCSVButton
                  data={donors.map(donor => ({
                    name: donor.isAnonymous ? "Anonymous" : donor.name,
                    amount: donor.amount,
                    date: new Date(donor.date).toLocaleDateString("en-US"),
                  }))}
                  headers={["name", "amount", "date"]}
                  filename="top-donors.csv"
                  label="Download - CSV.format"
                />
            )}
          </div>
          {showTotalDonors && <S.TotalDonorsNumber>{donors.length}</S.TotalDonorsNumber>}
        </S.TitleContainer>
        {showTopDonorAvatar && donors.length > 0 && (
          <S.TopDonorContainer>
            {showTopDonorName && (
              <span>{donors[0].isAnonymous ? "Anonymous" : donors[0].name}</span>
            )}
            <Avatar
              sx={{ width: "57px", height: "57px" }}
              src={!donors[0].isAnonymous ? donors[0].avatar : undefined}
            >
              {donors[0].isAnonymous && <PersonIcon />}
            </Avatar>
          </S.TopDonorContainer>
        )}
      </S.HeaderContainer>

      {displayedDonors.length > 0 ? (
        <S.DonorList $scroll={showAllDonors}>
          {displayedDonors.map((donor, key) => (
            <S.DonorRow key={key}>
              <span className="idx">{key + 1}.</span>
              <Avatar sx={{ width: 42, height: 42 }} src={!donor.isAnonymous ? donor.avatar : undefined}>
                {donor.isAnonymous && <PersonIcon />}
              </Avatar>
              <S.DonorInfo>
                <S.DonorName>
                  {donor.isAnonymous ? "Anonymous" : donor.name}
                </S.DonorName>
                <S.DonorDate>
                  {new Date(donor.date).toLocaleDateString("en-US", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                  })}
                </S.DonorDate>
              </S.DonorInfo>
              <S.DonorAmt>
                {donor.displayAmount === false ? (
                  <span
                    style={{
                      backgroundColor: '#e0e0e0',
                      borderRadius: '4px',
                      width: '80px',
                      height: '1em',
                      display: 'inline-block',
                      opacity: 0.7,
                      animation: 'pulse 1.5s infinite',
                      filter: 'blur(2px)',
                    }}
                  ></span>
                ) : (
                  `$${Number(donor.amount).toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}`
                )}
              </S.DonorAmt>
            </S.DonorRow>
          ))}
        </S.DonorList>
      ) : (
        <S.DonorsEmptyState>No donors found</S.DonorsEmptyState>
      )}

      {donors.length > donorsToShowInitially && (
        <S.DonorsMoreChevronButton
          onClick={() => setShowAllDonors(!showAllDonors)}
        >
          <DoubleChevronIcon rotated={showAllDonors} />
        </S.DonorsMoreChevronButton>
      )}
    </div>
  );
};

export default TopDonorsList; 